import { useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import { api } from "services";
import { toast } from "sonner";

const ReportsView = ({ setReportLoading, type = "pdf" }) => {
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(new Date());
  const [isDownloading, setIsDownloading] = useState(false);

  const formatDateInput = (e) => {
    const val = e?.target?.value;
    if (typeof val !== "string") return;
    const digits = val.replace(/\D/g, "").slice(0, 8);
    if (digits.length >= 5) {
      e.target.value = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    } else if (digits.length >= 3) {
      e.target.value = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
  };

  const getFinancialYear = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0 = Jan, 3 = Apr

    // FY starts in April
    return currentMonth >= 3 ? currentYear : currentYear - 1;
  };

  const handleDownload = async (start, end = null) => {
    try {
      if (start !== "lifetime" && (!start || (showCustomModal && !end))) {
        toast.error("Please select a complete date range.");
        return;
      }

      setIsDownloading(true);
      setReportLoading((prev) => ({ ...prev, [type]: true }));

      await api.getFYReport(start, end, type);

    } catch (error) {
      toast.error("Download Failed, Please try again later.");
      console.log(error);
    } finally {
      setReportLoading((prev) => ({ ...prev, [type]: false }));
      setIsDownloading(false);
      setShowCustomModal(false);
    }
  };

  const currentFYStart = getFinancialYear();
  const currentFY = currentFYStart - 2000;

  return (
    <>
      <div className="dropdown">
        <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
          <li>
            <button
              className="dropdown-item"
              onClick={() => handleDownload(new Date(currentFYStart, 3, 1), new Date())}
            >
              Current FY
            </button>
          </li>
          <li>
            <button
              className="dropdown-item"
              onClick={() => handleDownload(new Date(currentFYStart - 1, 3, 1))}
            >
              FY ({currentFY - 1}-{currentFY})
            </button>
          </li>
          <li>
            <button
              className="dropdown-item"
              onClick={() => handleDownload(new Date(currentFYStart - 2, 3, 1))}
            >
              FY ({currentFY - 2}-{currentFY - 1})
            </button>
          </li>
          <li>
            <button
              className="dropdown-item"
              onClick={() => handleDownload("lifetime")}
            >
              Lifetime
            </button>
          </li>
          <li>
            <button
              className="dropdown-item"
              onClick={() => setShowCustomModal(true)}
            >
              Custom
            </button>
          </li>
        </ul>
      </div>

      {showCustomModal &&
        createPortal(
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1060 }}
            onClick={(e) => {
              if (e.target === e.currentTarget && !isDownloading) {
                setShowCustomModal(false);
              }
            }}
          >
            <div className="modal-dialog modal-dialog-top" style={{ maxWidth: "360px", marginTop: "10%" }}>
              <div className="modal-content shadow border-0 rounded-3">
                <div className="modal-header py-2 px-3 align-items-center bg-app">
                  <h6 className="modal-title fw-bold m-0 text-light">
                    Select Date Range ({type.toUpperCase()})
                  </h6>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    aria-label="Close"
                    disabled={isDownloading}
                    onClick={() => setShowCustomModal(false)}
                  ></button>
                </div>

                <div className="modal-body text-start row my-3">
                  <div className="col-6">
                    <label className="form-label small fw-semibold text-muted mb-1">
                      Start Date
                    </label>
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      onChangeRaw={formatDateInput}
                      maxDate={new Date()}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="dd/mm/yyyy"
                      className="form-control px-0 py-1"
                      wrapperClassName="w-100"
                      showYearDropdown
                      showMonthDropdown
                      calendarClassName="hide-header"
                      yearDropdownItemNumber={2}
                      popperPlacement="bottom-end"
                    />
                  </div>

                  <div className="col-6">
                    <label className="form-label small fw-semibold text-muted mb-1">
                      End Date
                    </label>
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => setEndDate(date)}
                      onChangeRaw={formatDateInput}
                      minDate={startDate}
                      maxDate={new Date()}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="dd/mm/yyyy"
                      className="form-control px-0 py-1"
                      wrapperClassName="w-100"
                      showYearDropdown
                      showMonthDropdown
                      calendarClassName="hide-header"
                      yearDropdownItemNumber={2}
                      popperPlacement="bottom-start"
                    />
                  </div>
                </div>

                <div className="modal-footer py-2 px-3 d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-sm btn-app px-3"
                    disabled={!startDate || !endDate || isDownloading}
                    onClick={() => handleDownload(startDate, endDate)}
                  >
                    {isDownloading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        />
                        Downloading...
                      </>
                    ) : (
                      `Download ${type.toUpperCase()}`
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default ReportsView;